import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, FileText, PenLine, Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SavedCardPay from "@/components/SavedCardPay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const STAGE_LABELS = {
  lead_in: "Lead", in_progress: "In corso", assigned: "Assegnato",
  completed: "Completato", commission_paid: "Provvigione pagata",
};

function QuoteCard({ q, project, me, reload }) {
  const [signature, setSignature] = useState("");
  const [signing, setSigning] = useState(false);
  const isAuthor = project?.author_id === me?.id;

  const accept = async () => {
    if (!signature.trim()) return toast.error("Inserisci la tua firma");
    try { await api.post(`/quotes/${q.id}/accept`, { signature }); toast.success("Preventivo firmato"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const decline = async () => { await api.post(`/quotes/${q.id}/decline`); toast.success("Preventivo rifiutato"); reload(); };
  const checkout = async (useCashback = false) => {
    try { const { data } = await api.post(`/payments/quote/${q.id}/checkout`, { origin_url: window.location.origin, use_cashback: useCashback === true }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`quote-${q.id}`} className="border border-neutral-200 rounded-2xl p-4">
      <div className="flex items-center justify-between">
        <span className="font-display text-xl font-extrabold">€{Number(q.amount).toFixed(2)}</span>
        <span className={`text-[11px] rounded-full px-2 py-0.5 ${q.status === "paid" ? "bg-emerald-100 text-emerald-700" : q.status === "accepted" ? "bg-blue-100 text-blue-700" : q.status === "declined" ? "bg-red-100 text-red-600" : "bg-neutral-100"}`}>
          {q.status === "paid" ? "Pagato" : q.status === "accepted" ? "Firmato" : q.status === "declined" ? "Rifiutato" : "Inviato"}
        </span>
      </div>
      {q.items?.length > 0 && (
        <ul className="mt-2 space-y-1">
          {q.items.map((it, i) => <li key={i} className="flex justify-between text-sm"><span className="text-neutral-600">{it.label}</span><span className="font-mono">€{Number(it.price).toFixed(2)}</span></li>)}
        </ul>
      )}
      {q.notes && <p className="text-sm text-neutral-500 mt-2">{q.notes}</p>}
      {q.signature && <p className="text-xs text-neutral-400 mt-2 italic">Firmato da: {q.signature}</p>}

      {isAuthor && q.status === "sent" && (
        <div className="mt-3 border-t border-neutral-100 pt-3">
          {!signing ? (
            <div className="flex gap-2">
              <Button data-testid={`quote-sign-${q.id}`} onClick={() => setSigning(true)} className="flex-1 h-10 rounded-full bg-black text-white text-sm"><PenLine size={15} className="mr-1" />Accetta e firma</Button>
              <Button data-testid={`quote-decline-${q.id}`} onClick={decline} variant="outline" className="h-10 rounded-full border-red-200 text-red-600 text-sm">Rifiuta</Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Input data-testid={`quote-signature-${q.id}`} value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Scrivi il tuo nome per firmare" className="h-11" />
              <Button data-testid={`quote-accept-${q.id}`} onClick={accept} className="w-full h-10 rounded-full bg-black text-white text-sm">Conferma firma</Button>
            </div>
          )}
        </div>
      )}

      {isAuthor && q.status === "accepted" && (
        <div className="mt-3 border-t border-neutral-100 pt-3">
          <SavedCardPay endpoint={`/payments/quote/${q.id}/pay-saved`} amount={Number(q.amount)} label="Paga preventivo" checkoutLabel="Paga con carta" onPaid={reload} onCheckout={checkout} />
        </div>
      )}
      {q.status === "paid" && <p className="text-emerald-600 font-semibold text-sm mt-3">Pagato ✓</p>}
    </div>
  );
}

export default function ProjectDetailPage() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [msg, setMsg] = useState("");
  const [qAmount, setQAmount] = useState("");
  const [qNotes, setQNotes] = useState("");
  const [items, setItems] = useState([{ uid: Math.random().toString(36).slice(2), label: "", price: "" }]);
  const itemsTotal = items.reduce((s, it) => s + (parseFloat(it.price) || 0), 0);

  const load = async () => {
    try {
      const [p, m, q] = await Promise.all([
        api.get(`/projects/${pid}`), api.get(`/projects/${pid}/messages`), api.get(`/projects/${pid}/quotes`),
      ]);
      setProject(p.data); setMessages(m.data); setQuotes(q.data);
    } catch { toast.error("Progetto non trovato"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const send = async () => {
    if (!msg.trim()) return;
    const { data } = await api.post(`/projects/${pid}/messages`, { text: msg });
    setMessages([...messages, data]); setMsg("");
  };
  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => setItems([...items, { uid: Math.random().toString(36).slice(2), label: "", price: "" }]);
  const removeItem = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);
  const createQuote = async () => {
    const validItems = items.filter((it) => it.label.trim() && parseFloat(it.price) > 0)
      .map((it) => ({ label: it.label.trim(), price: parseFloat(it.price) }));
    const amount = validItems.length ? validItems.reduce((s, it) => s + it.price, 0) : parseFloat(qAmount) || 0;
    if (amount <= 0) return toast.error("Aggiungi almeno una voce con prezzo");
    try {
      await api.post(`/projects/${pid}/quote`, { amount, items: validItems, notes: qNotes });
      setQAmount(""); setQNotes(""); setItems([{ uid: Math.random().toString(36).slice(2), label: "", price: "" }]);
      toast.success("Preventivo inviato"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!project) return <p className="text-center text-neutral-400 py-10 text-sm">Caricamento…</p>;

  return (
    <div className="p-4">
      <button onClick={() => navigate("/pipeline")} className="flex items-center gap-1 text-sm text-neutral-500 mb-3"><ArrowLeft size={18} />Pipeline</button>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">{project.title}</h1>
        <span className="text-[11px] bg-neutral-100 rounded-full px-2 py-0.5">{STAGE_LABELS[project.stage]}</span>
      </div>
      <p className="text-sm text-neutral-500 mt-1">di {project.author?.name}</p>
      <p className="text-sm mt-2 whitespace-pre-wrap">{project.description}</p>
      {project.budget > 0 && <p className="text-sm font-mono mt-1">Budget: €{project.budget}</p>}

      {/* Quotes */}
      <div className="mt-6">
        <h2 className="font-display font-bold mb-2 flex items-center gap-2"><FileText size={18} />Preventivi</h2>
        {quotes.length === 0 && <p className="text-xs text-neutral-400">Nessun preventivo ancora.</p>}
        <div className="space-y-3">
          {quotes.map((q) => <QuoteCard key={q.id} q={q} project={project} me={user} reload={load} />)}
        </div>

        {user?.role === "admin" && (
          <div className="mt-4 border border-dashed border-neutral-300 rounded-2xl p-4">
            <p className="text-sm font-semibold mb-2">Crea preventivo</p>
            <div className="space-y-2 mb-2">
              {items.map((it, i) => (
                <div key={it.uid} className="flex gap-2 items-center">
                  <Input data-testid={`quote-item-label-${i}`} placeholder="Voce (es. Progettazione)" value={it.label} onChange={(e) => setItem(i, "label", e.target.value)} className="h-10 flex-1" />
                  <Input data-testid={`quote-item-price-${i}`} type="number" placeholder="€" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} className="h-10 w-24" />
                  <button data-testid={`quote-item-remove-${i}`} onClick={() => removeItem(i)} className="text-neutral-300 hover:text-red-500 p-1"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <button data-testid="quote-add-item" onClick={addItem} className="text-xs text-neutral-600 flex items-center gap-1 mb-3"><Plus size={14} />Aggiungi voce</button>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-neutral-500">Totale</span>
              <span className="font-display font-bold" data-testid="quote-total">€{itemsTotal.toFixed(2)}</span>
            </div>
            <Input data-testid="quote-notes" placeholder="Note (opzionale)" value={qNotes} onChange={(e) => setQNotes(e.target.value)} className="h-10 mb-2" />
            <Button data-testid="quote-create-btn" onClick={createQuote} className="w-full h-11 rounded-full bg-black text-white">Invia preventivo</Button>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="mt-6">
        <h2 className="font-display font-bold mb-2">Chat progetto</h2>
        <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
          {messages.length === 0 && <p className="text-xs text-neutral-400">Nessun messaggio.</p>}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.author_id === user.id ? "justify-end" : "justify-start"}`}>
              <span className={`text-sm rounded-2xl px-3 py-1.5 max-w-[75%] ${m.author_id === user.id ? "bg-black text-white" : "bg-neutral-100"}`}>{m.text}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input data-testid="project-msg-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Messaggio…" className="h-10" />
          <Button data-testid="project-msg-send" onClick={send} className="h-10 w-10 p-0 rounded-full bg-black"><Send size={16} /></Button>
        </div>
      </div>
    </div>
  );
}
